import { FC, Fragment, useCallback, useMemo, useState } from 'react';
import { ChevronLeft } from '@eqi-investimentos/icons/web';
import { Button, Flex, Spinner, Text, useToast } from '@eqi-investimentos/react';
import { Divider, Radio, RadioGroup } from '@chakra-ui/react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { REGISTER_FORM_FACTORY, type RegisterFormQuestion, type RegisterFormRadioSelect, type RegisterFormType } from '../../interface/RegisterForm';
import { formQuestions } from '@/config/cms/fpRegisterForm';
import { useAuth } from '@/contexts/AuthContext';
import { IconButton } from '@/components/atoms/IconButton';
import { IfRender } from '@/utils/jsx';
import { toastConfigDefault } from '@/utils/toast';
import { useSendAddFinancialPlanner } from '../../http/client/mutations/useSendAddFinancialPlanner';
import { CoverScreens } from './CoverScreens';
import { DependentsFields } from './Fields/DependentsFields';
import { SpouseFields } from './Fields/SpouseFields';
import { FINANCIAL_PLANNING_REGISTER } from './RegisterDrawer';
import { RegisterFormSchema } from './schema';
export const RegisterForm: FC<RegisterFormProps> = ({
  step,
  setStep,
  invalidateQuery
}) => {
  const [subStep, setSubStep] = useState(0);
  const [isSubmiting, setIsSubmiting] = useState(false);
  const {
    user
  } = useAuth();
  const {
    mutateAsync: mutateAddFP
  } = useSendAddFinancialPlanner();
  const toast = useToast(toastConfigDefault.primary);
  const isFirstScreen = step === 0;
  const isLastScreen = step === 4;
  const {
    control,
    formState: {
      errors
    },
    getValues,
    reset,
    resetField,
    setValue,
    watch
  } = useForm<RegisterFormType>({
    defaultValues: REGISTER_FORM_FACTORY,
    resolver: yupResolver(RegisterFormSchema),
    mode: 'onChange'
  });
  const currentQuestion = useMemo(() => {
    if (isFirstScreen || isLastScreen) return {} as RegisterFormQuestion;
    return formQuestions[step][subStep];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, subStep]);
  const isStepInvalid = useCallback(() => {
    const fieldId = currentQuestion.id;
    const values = watch(fieldId);
    if (!values) return true;
    const normalizeToArray = (value: unknown) => {
      if (value === undefined || value === null) return [];
      return Array.isArray(value) ? value : [value];
    };
    const valuesArray = normalizeToArray(values);
    const errorArray = normalizeToArray(errors[fieldId]);
    const isAnyNestedTouched = valuesArray.every(value => value && typeof value === 'object' ? currentQuestion.fields!.every(({
      id
    }) => !!(value as Record<string, unknown>)?.[id]) : !!value);
    const hasNestedErrors = errorArray.some(error => error && typeof error === 'object' ? Object.keys(error).length > 0 : !!error);
    return hasNestedErrors || !isAnyNestedTouched;
  }, [currentQuestion, watch, errors]);
  const handleClose = () => {
    reset();
    setStep(0);
    setSubStep(0);
    window.dispatchEvent(new Event(`close-chakra-modal-${FINANCIAL_PLANNING_REGISTER}`));
  };
  const handlePrevStep = () => {
    resetField(currentQuestion.id);
    if (subStep > 0) {
      if (step === 2 && getValues('maritalStatus') === 'STABLE_UNION') {
        return setSubStep(0);
      }
      return setSubStep(prev => prev - 1);
    }
    if (step === 3) {
      if (!!getValues('spouse')) {
        setStep(2);
        return setSubStep(2);
      }
    }
    setStep(step - 1);
    setSubStep(0);
  };
  const handleNextStep = () => {
    if (step === 2) {
      if (subStep === 0) {
        if (getValues('maritalStatus') === 'MARRIED') return setSubStep(1);
        if (getValues('maritalStatus') === 'STABLE_UNION') return setSubStep(2);
      }
      if (subStep === 1) return setSubStep(2);
    }
    if (step === 3) {
      if (subStep === 0 && getValues('hasDependents') === 'true') return setSubStep(1);
      return handleSubmit();
    }
    setStep(step + 1);
    setSubStep(0);
  };
  const handleSubmit = async () => {
    setIsSubmiting(true);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const {
      hasDependents,
      ...formattedData
    } = getValues();
    const cleanObject = Object.fromEntries(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    Object.entries(formattedData).filter(([_, value]) => {
      if (!Array.isArray(value)) return value !== undefined;
      return value.some(obj => Object.values(obj).some(v => {
        if (typeof v === 'string') return v.trim() !== '';
        if (typeof v === 'number') return !isNaN(v);
        return v !== null && v !== undefined;
      }));
    }));
    try {
      await mutateAddFP({
        ...cleanObject,
        name: !user ? '' : user.name
      });
      invalidateQuery();
      setStep(4);
      setSubStep(0);
    } catch (e) {
      toast({
        status: 'error',
        description: 'Ocorreu um erro ao criar seu planejamento. Por favor, tente novamente mais tarde.'
      });
    } finally {
      setIsSubmiting(false);
    }
  };
  return <Flex direction="column" justifyContent="space-between" h="full" data-sentry-element="Flex" data-sentry-component="RegisterForm" data-sentry-source-file="RegisterForm.tsx">
      <Flex as="form" p="0.56rem 1.44rem" data-sentry-element="Flex" data-sentry-source-file="RegisterForm.tsx">
        <IfRender condition={isFirstScreen || isLastScreen} data-sentry-element="IfRender" data-sentry-source-file="RegisterForm.tsx">
          <CoverScreens {...{
          isFirstScreen,
          isLastScreen
        }} data-sentry-element="CoverScreens" data-sentry-source-file="RegisterForm.tsx" />
        </IfRender>
        <IfRender condition={!isFirstScreen && !isLastScreen} data-sentry-element="IfRender" data-sentry-source-file="RegisterForm.tsx">
          <IconButton ariaLabel="Retornar" icon={ChevronLeft} onButtonClick={handlePrevStep} buttonProps={{
          pos: 'absolute',
          top: '3.25rem',
          left: 5
        }} iconProps={{
          w: 24,
          h: 24,
          color: 'content.brand'
        }} data-sentry-element="IconButton" data-sentry-source-file="RegisterForm.tsx" />
          <Flex direction="column" gap={32} w="full" data-sentry-element="Flex" data-sentry-source-file="RegisterForm.tsx">
            <Text fontSize="lg" lineHeight={8} data-sentry-element="Text" data-sentry-source-file="RegisterForm.tsx">
              {currentQuestion.text}
            </Text>
            <IfRender condition={!!currentQuestion.options} data-sentry-element="IfRender" data-sentry-source-file="RegisterForm.tsx">
              <Controller name={currentQuestion.id as RegisterFormRadioSelect} control={control}
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            render={({
              field: {
                ref,
                value,
                ...props
              }
            }) => <RadioGroup key={currentQuestion.id} display="flex" flexDirection="column" gap={6} colorScheme="brand" value={getValues(currentQuestion.id as RegisterFormRadioSelect)} sx={{
              '.chakra-radio': {
                h: '68px'
              },
              '& .chakra-radio__control': {
                w: 5,
                h: 5
              }
            }} {...props}>
                    {currentQuestion.options?.map(({
                name,
                value
              }, idx) => <Fragment key={name}>
                        <IfRender condition={idx > 0}>
                          <Divider w="full" opacity={1} borderColor="var(--color-eqi-white-darken-4)" />
                        </IfRender>
                        <Radio value={value} flexDirection="row-reverse" justifyContent="space-between">
                          {name}
                        </Radio>
                      </Fragment>)}
                  </RadioGroup>} data-sentry-element="Controller" data-sentry-source-file="RegisterForm.tsx" />
            </IfRender>
            <IfRender condition={currentQuestion.id === 'spouse'} data-sentry-element="IfRender" data-sentry-source-file="RegisterForm.tsx">
              <SpouseFields fields={currentQuestion.fields!} {...{
              errors,
              control,
              setValue
            }} data-sentry-element="SpouseFields" data-sentry-source-file="RegisterForm.tsx" />
            </IfRender>
            <IfRender condition={currentQuestion.id === 'dependents'} data-sentry-element="IfRender" data-sentry-source-file="RegisterForm.tsx">
              <DependentsFields fields={currentQuestion.fields!} {...{
              errors,
              control
            }} data-sentry-element="DependentsFields" data-sentry-source-file="RegisterForm.tsx" />
            </IfRender>
          </Flex>
        </IfRender>
      </Flex>
      <Flex p="2rem 1.25rem" borderTopWidth="1px" borderColor="var(--color-eqi-white-2)" w="full" data-sentry-element="Flex" data-sentry-source-file="RegisterForm.tsx">
        <Button variant={isLastScreen ? 'outline' : 'solid'} onClick={isLastScreen ? handleClose : handleNextStep} justifyContent="center" h="3.25rem" w="full" borderRadius="base" isDisabled={!isFirstScreen && !isLastScreen ? isStepInvalid() : false} isLoading={isSubmiting} sx={{
        '.chakra-spinner': {
          color: 'content.alwaysLight'
        }
      }} data-sentry-element="Button" data-sentry-source-file="RegisterForm.tsx">
          {isLastScreen && !isSubmiting ? 'Fazer isso mais tarde' : 'Continuar'}
          {isSubmiting && <Spinner size="sm" />}
        </Button>
      </Flex>
    </Flex>;
};
type RegisterFormProps = {
  step: number;
  setStep: (step: number) => void;
  invalidateQuery: () => void;
};